/* eslint-disable no-unused-vars */
/* eslint-disable indent */
/* eslint-disable */
import React, { useState, useEffect, useRef } from "react";
import { Card, Row, Col, message, Input, Button } from "antd";

import { DownloadOutlined } from "@ant-design/icons";
import moment from "moment";
import {
    DropDownComponent,
    TableComponent,
    CalendarComponent,
} from "../../CommonComponents";
import ReportModernViewWrapper from "../ReportModernViewWrapper";
import "../styles.scss";
import Heading from "../../../../components/Heading";
import { getHubList } from "../../../../services/api/hub";
import { ReportsService } from "../../../../services/api/reports";
import { downloadIncomingBagReport } from "../../../../utils/downloadUtils";
import { useRequestCancellation } from "../../../../hooks/useRequestCancellation";
import { useNavigate } from "react-router-dom";

const { TextArea } = Input;

const IncomingBagReport = () => {
    const FILTER_STORAGE_KEY = "INCOMING_BAG_REPORT_FILTERS";

    const [loading, setLoading] = useState(false);
    const [downloadLoading, setDownloadLoading] = useState(false);
    const [dateRange, setDateRange] = useState([
        moment().subtract(1, "days"),
        moment()
    ]);
    const [hubList, setHubList] = useState([]);
    const [selectedFromHubs, setSelectedFromHubs] = useState([]);
    const [selectedToHubs, setSelectedToHubs] = useState([]);
    const [bagNumbers, setBagNumbers] = useState("");
    const [transportTypes, setTransportTypes] = useState([]);
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 10,
        total: 0,
        showSizeChanger: true,
        showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`
    });
    const [reportData, setReportData] = useState([]);
    const [hubUser, setHubUser] = useState();
    const [headerHubList, setHeaderHubList] = useState([]);
    const [lastsynced, setLastsynced] = useState(null);
    const [nextScheduledRefresh, setNextScheduledRefresh] = useState(null);
    const [activeHubId, setActiveHubId] = useState(JSON.parse(localStorage.getItem("active_hub") || "{}")?.hubId || "ALL_HUBS");

    // Fetch hubs from header/localStorage
    useEffect(() => {
        try {
            const userFullData = JSON.parse(localStorage.getItem("userId") || "{}");
            const userHubs = userFullData?.userInfo?.hubDetails || [];
            if (userHubs?.length > 0) {
                const headerHubs = [
                    { label: "All", value: "ALL" },
                    ...userHubs.map(hub => ({
                        label: hub.hubName,
                        value: hub.hubId
                    }))
                ];
                setHeaderHubList(headerHubs);
            } else {
                // If no hubs assigned, show all available hubs but without "All" option
                setHeaderHubList([]);
            }
        } catch (e) {
            console.error("Error parsing user hubs for Incoming Bag Report", e);
            setHeaderHubList([]);
        }
    }, []);

    const handleMultiSelectChange = (values, setter, options = []) => {
        if (values.includes("ALL")) {
            const isLatestAll = values[values.length - 1] === "ALL";
            if (isLatestAll) {
                // Select all actual values (excluding the "ALL" option itself)
                const allActualValues = options
                    .filter(opt => opt.value !== "ALL")
                    .map(opt => opt.value);
                setter(allActualValues);
            } else {
                // If it wasn't the latest click, just filter it out (though shouldn't happen with this logic)
                setter(values.filter(v => v !== "ALL"));
            }
        } else {
            setter(values);
        }
    };

    // Ref to track whether we already set the hub for the current login session
    const hubInitializedRef = useRef(false);
    const navigate = useNavigate();

    // Helper function to save filters to sessionStorage
    const saveFiltersToStorage = () => {
        const filters = {
            dateRange: dateRange ? [
                dateRange[0]?.format("YYYY-MM-DD"),
                dateRange[1]?.format("YYYY-MM-DD")
            ] : null,
            selectedFromHubs,
            selectedToHubs,
            bagNumbers,
            transportTypes,
            pagination: {
                current: pagination.current,
                pageSize: pagination.pageSize
            }
        };
        sessionStorage.setItem(FILTER_STORAGE_KEY, JSON.stringify(filters));
    };

    // Helper function to restore filters from sessionStorage
    const restoreFiltersFromStorage = () => {
        try {
            const savedFilters = sessionStorage.getItem(FILTER_STORAGE_KEY);
            if (savedFilters) {
                const filters = JSON.parse(savedFilters);
                if (filters.dateRange && filters.dateRange[0] && filters.dateRange[1]) {
                    setDateRange([
                        moment(filters.dateRange[0]),
                        moment(filters.dateRange[1])
                    ]);
                }
                if (filters.selectedFromHubs && filters.selectedFromHubs.length > 0) {
                    setSelectedFromHubs(filters.selectedFromHubs);
                }
                if (filters.selectedToHubs && filters.selectedToHubs.length > 0) {
                    setSelectedToHubs(filters.selectedToHubs);
                }
                if (filters.bagNumbers) {
                    setBagNumbers(filters.bagNumbers);
                }
                if (filters.transportTypes && filters.transportTypes.length > 0) {
                    setTransportTypes(filters.transportTypes);
                }
                if (filters.pagination) {
                    setPagination(prev => ({
                        ...prev,
                        current: filters.pagination.current || prev.current,
                        pageSize: filters.pagination.pageSize || prev.pageSize
                    }));
                }
                // Clear the saved filters after restoring
                sessionStorage.removeItem(FILTER_STORAGE_KEY);
                return true;
            }
        } catch (error) {
            console.error("Error restoring filters:", error);
        }
        return false;
    };

    const transportTypeOptions = [
        { label: "SURFACE", value: "SURFACE" },
        { label: "AIR", value: "AIR" }
    ];

    useEffect(() => {
        (async () => {
            try {
                setLoading(true);
                const hubs = await getHubList();
                setHubList(hubs);
                // Restore filters after hub list is loaded
                restoreFiltersFromStorage();
            } catch {
                message.error("Failed to fetch hub list");
            } finally {
                // setLoading(false);
            }
        })();
    }, []);

    // Sync with App Header hub changes
    useEffect(() => {
        const handleHubChanged = () => {
            const activeHub = JSON.parse(localStorage.getItem("active_hub") || "{}");
            const newActiveHubId = activeHub?.hubId;
            setActiveHubId(newActiveHubId || "ALL_HUBS");
            if (newActiveHubId && newActiveHubId !== "ALL_HUBS") {
                setSelectedToHubs([newActiveHubId]);
            } else {
                setSelectedToHubs(["ALL"]);
            }
        };

        window.addEventListener("hubChanged", handleHubChanged);
        return () => window.removeEventListener("hubChanged", handleHubChanged);
    }, []);

    // Set default hub based on logged-in user — always enforced for hub-restricted roles
    useEffect(() => {
        const user = JSON.parse(localStorage.getItem("userinfo") || "{}");
        const role = user?.roles?.[0];
        setHubUser(role);

        // Check if there are saved filters - if yes, don't set default hub (restore will handle it)
        const hasSavedFilters = sessionStorage.getItem(FILTER_STORAGE_KEY);

        const activeHub = JSON.parse(localStorage.getItem("active_hub") || "{}");
        const currentActiveHubId = activeHub?.hubId || "ALL_HUBS";

        if (!hubInitializedRef.current && !hasSavedFilters) {
            const userFullData = JSON.parse(localStorage.getItem("userId") || "{}");
            const userHubs = userFullData?.userInfo?.hubDetails || [];

            if (currentActiveHubId && currentActiveHubId !== "ALL_HUBS") {
                setSelectedToHubs([currentActiveHubId]);
            } else if (userHubs?.length === 0 || role === "ROLE_HOADMIN" || role === "ROLE_CENTRALOPERATOR") {
                setSelectedToHubs([]);
            } else {
                setSelectedToHubs(["ALL"]);
            }
            hubInitializedRef.current = true;
        }
    }, [headerHubList]); // Depend on headerHubList to ensure we have options available

    useEffect(() => {
        if (!hubUser) {
            return;
        }
        if ((hubUser === "ROLE_HUBADMIN" || hubUser === "ROLE_HUBOPERATOR") && selectedToHubs.length === 0) {
            return;
        }
        fetchReport();
        // eslint-disable-next-line
    }, [hubUser, JSON.stringify(selectedFromHubs), JSON.stringify(selectedToHubs), JSON.stringify(transportTypes), dateRange, bagNumbers, pagination.current, pagination.pageSize]);

    const fetchReport = async () => {
        if (!dateRange[0] || !dateRange[1]) {
            message.warning("Please select date range");
            return;
        }

        setLoading(true);
        try {
            // Resolve Hubs (selectedFromHubs, selectedToHubs) for API logic
            const isSpecialRole = hubUser === "ROLE_HOADMIN" || hubUser === "ROLE_CENTRALOPERATOR";
            const activeHubOptions = isSpecialRole ? hubList : (headerHubList?.length > 0 ? headerHubList : hubList);

            const resolvedFromHubIds = selectedFromHubs?.includes("ALL")
                ? activeHubOptions.filter(opt => opt.value !== "ALL").map(opt => opt.value)
                : selectedFromHubs;

            const resolvedToHubIds = selectedToHubs?.includes("ALL")
                ? activeHubOptions.filter(opt => opt.value !== "ALL").map(opt => opt.value)
                : selectedToHubs;

            const selectedFromHubData = activeHubOptions.filter(hub => resolvedFromHubIds?.includes(hub.value));
            const selectedToHubData = activeHubOptions.filter(hub => resolvedToHubIds?.includes(hub.value));
            const bagNumberList = bagNumbers.split("\n").filter(num => num.trim());

            const params = {
                pageSize: pagination.pageSize,
                pageNum: pagination.current - 1,
                startDate: dateRange[0].format("YYYY-MM-DD"),
                endDate: dateRange[1].format("YYYY-MM-DD"),
                ...(bagNumberList.length > 0 && { bagNumbers: bagNumberList }),
                ...(selectedFromHubData?.length > 0 && { fromHubs: selectedFromHubData.map(hub => hub.label) }),
                ...(selectedToHubData?.length > 0 && { toHubs: selectedToHubData.map(hub => hub.label) }),
                ...(transportTypes?.length > 0 && { transportTypes })
            };

            const response = await ReportsService.getIncomingBagReport(params);
            if (response?.lastRefreshedAt) {
                setLastsynced(response?.lastRefreshedAt);
            }
            if (response?.nextScheduledRefresh) {
                setNextScheduledRefresh(response?.nextScheduledRefresh);
            }

            if (response?.data) {
                setReportData(response.data);
                setPagination(prev => ({
                    ...prev,
                    total: response.total || 0,
                    current: (response.page || 0) + 1,
                    pageSize: response.pageSize || 10
                }));
            } else {
                setReportData([]);
                setPagination(prev => ({
                    ...prev,
                    total: 0
                }));
                message.error("No data available");
            }
            setLoading(false);
        } catch (error) {
            message.error(error?.message || "Failed to fetch report data");
            setReportData([]);
            setPagination(prev => ({
                ...prev,
                total: 0
            }));
        } finally {
            setLoading(false);
        }
    };

    const handleTableChange = (newPagination) => {
        setPagination(prev => ({
            ...prev,
            current: newPagination.current,
            pageSize: newPagination.pageSize
        }));
    };

    const handleDownload = async () => {
        if (!dateRange[0] || !dateRange[1]) {
            message.warning("Please select date range");
            return;
        }
        setDownloadLoading(true);
        try {
            // Resolve Hubs (selectedFromHubs, selectedToHubs) for Download logic
            const isSpecialRole = hubUser === "ROLE_HOADMIN" || hubUser === "ROLE_CENTRALOPERATOR";
            const activeHubOptions = isSpecialRole ? hubList : (headerHubList?.length > 0 ? headerHubList : hubList);

            const resolvedFromHubIds = selectedFromHubs?.includes("ALL")
                ? activeHubOptions.filter(opt => opt.value !== "ALL").map(opt => opt.value)
                : selectedFromHubs;

            const resolvedToHubIds = selectedToHubs?.includes("ALL")
                ? activeHubOptions.filter(opt => opt.value !== "ALL").map(opt => opt.value)
                : selectedToHubs;

            const selectedFromHubData = activeHubOptions.filter(hub => resolvedFromHubIds?.includes(hub.value));
            const selectedToHubData = activeHubOptions.filter(hub => resolvedToHubIds?.includes(hub.value));
            const bagNumberList = bagNumbers
                .split(/[\n,]/) // Split by newlines or commas
                .map(num => num.trim())
                .filter(num => num.length > 0);

            const downloadParams = {
                startDate: dateRange[0].format("YYYY-MM-DD"),
                endDate: dateRange[1].format("YYYY-MM-DD"),
                ...(bagNumberList.length > 0 && { bagNumbers: bagNumberList }),
                ...(selectedFromHubData?.length > 0 && { fromHubs: selectedFromHubData.map(hub => hub.label) }),
                ...(selectedToHubData?.length > 0 && { toHubs: selectedToHubData.map(hub => hub.label) }),
                ...(transportTypes?.length > 0 && { transportTypes })
            };

            await downloadIncomingBagReport(downloadParams);
            message.success("Download started successfully");
        } catch (error) {
            message.error("Failed to download report");
        } finally {
            setDownloadLoading(false);
        }
    };

    const handleCellClick = (record) => {
        // Save current filters before navigating
        saveFiltersToStorage();

        const navigationState = {
            type: "Incoming",
            url: "/incoming-bag-report",
            bagBarcodeNo: record.bagno
        };

        navigate("/incoming-outgoing-shipment-details", {
            state: navigationState
        });
    };

    const columns = [
        {
            title: "No.",
            dataIndex: "index",
            key: "index",
            width: 60,
            render: (_, __, index) => (pagination.current - 1) * pagination.pageSize + index + 1,
        },
        { title: "Date", dataIndex: "date", key: "date", sorter: true, width: 120 },
        {
            title: "Bag Number", dataIndex: "bagno", key: "bagno", sorter: true, width: 140,
            render: (text, record) => (
                <span
                    style={{ color: "#1890ff", cursor: "pointer", textDecoration: "underline" }}
                    onClick={() => {
                        const url = `/shipment-tracker?awb=${text}`;
                        window.open(url, "_blank");
                    }}
                >
                    {text}
                </span>
            )
        },
        { title: "From Hub", dataIndex: "fromHub", key: "fromHub", sorter: true, width: 180 },
        { title: "To Hub", dataIndex: "toHub", key: "toHub", sorter: true, width: 180 },
        { title: "Mode of Transport", dataIndex: "modeOfTransport", key: "modeOfTransport", sorter: true, width: 140 },
        {
            title: "Total Shipments",
            dataIndex: "totalShipmentCount",
            key: "totalShipmentCount",
            sorter: true,
            width: 120,
            render: (text, record) => (
                text && text > 0 ? (
                    <span
                        style={{
                            color: "#1890ff",
                            cursor: "pointer",
                            textDecoration: "underline"
                        }}
                        onClick={() => handleCellClick(record)}
                    >
                        {text}
                    </span>
                ) : <span>{text || 0}</span>
            )
        },
        { title: "Bag Weight (kg)", dataIndex: "bagWeight", key: "bagWeight", sorter: true, width: 120 },

    ];

    return (
        <ReportModernViewWrapper
            title="Incoming Bag Report"
            navigateTo="/reports"
            newReportUrl="/reports/incoming-bag-report/"
            lastsynced={lastsynced}
            nextScheduledRefresh={nextScheduledRefresh}
        >
            <Card className="report-card">
                <Row gutter={[16, 16]} className="filters-section">
                    <Col xs={24} sm={12} md={8} lg={6}>
                        <div className="filter-label">Date Range</div>
                        <CalendarComponent
                            className="custom-calendar"
                            type="range"
                            value={dateRange}
                            onChange={setDateRange}
                            format="YYYY-MM-DD"
                            allowClear
                        />
                    </Col>
                    <Col xs={24} sm={12} md={8} lg={6}>
                        <div className="filter-label">From Hub</div>
                        <DropDownComponent
                            className="small-dropdown"
                            placeholder="Select From Hubs"
                            options={(hubUser === "ROLE_HOADMIN" || hubUser === "ROLE_CENTRALOPERATOR") ? hubList.filter(o => o.value !== "ALL") : (headerHubList?.length > 0 ? headerHubList : hubList)}
                            onChange={(val) => {
                                const activeOptions = (hubUser === "ROLE_HOADMIN" || hubUser === "ROLE_CENTRALOPERATOR") ? hubList.filter(o => o.value !== "ALL") : (headerHubList?.length > 0 ? headerHubList : hubList);
                                handleMultiSelectChange(val, setSelectedFromHubs, activeOptions);
                                setPagination(prev => ({ ...prev, current: 1 }));
                            }}
                            value={selectedFromHubs}
                            mode="multiple"
                            allowClear
                            maxTagCount={1}
                            maxTagPlaceholder={(omittedValues) => `... +${omittedValues.length}`}
                            showSearch={true}
                            optionFilterProp="label"
                            filterOption={(input, option) =>
                                (option?.label || "").toLowerCase().includes(input.toLowerCase())
                            }
                        />
                    </Col>
                    <Col xs={24} sm={12} md={8} lg={6}>
                        <div className="filter-label">To Hub</div>
                        <DropDownComponent
                            className="small-dropdown"
                            placeholder="Select To Hubs"
                            options={(hubUser === "ROLE_HOADMIN" || hubUser === "ROLE_CENTRALOPERATOR") ? hubList.filter(o => o.value !== "ALL") : (headerHubList?.length > 0 ? headerHubList : hubList)}
                            onChange={(val) => {
                                const baseOptions = (hubUser === "ROLE_HOADMIN" || hubUser === "ROLE_CENTRALOPERATOR") ? hubList.filter(o => o.value !== "ALL") : (headerHubList?.length > 0 ? headerHubList : hubList);
                                handleMultiSelectChange(val, setSelectedToHubs, baseOptions);
                                setPagination(prev => ({ ...prev, current: 1 }));
                            }}
                            value={selectedToHubs}
                            allowClear
                            mode="multiple"
                            maxTagCount={1}
                            maxTagPlaceholder={(omittedValues) => `... +${omittedValues.length}`}
                            showSearch={true}
                            optionFilterProp="label"
                            filterOption={(input, option) =>
                                (option?.label || "").toLowerCase().includes(input.toLowerCase())
                            }
                        />
                    </Col>
                    <Col xs={24} sm={12} md={8} lg={6}>
                        <div className="filter-label">Transport Type</div>
                        <DropDownComponent
                            placeholder="Transport Type"
                            options={transportTypeOptions}
                            onChange={setTransportTypes}
                            value={transportTypes}
                            allowClear
                            mode="multiple"
                            maxTagCount="responsive"
                        />
                    </Col>
                    <Col xs={24} sm={12} md={8} lg={6}>
                        <div className="filter-label">Bag Numbers</div>
                        <TextArea
                            placeholder="Enter bag numbers (comma or newline separated: 112,233,322)"
                            value={bagNumbers}
                            onChange={(e) => setBagNumbers(e.target.value)}
                            autoSize={{ minRows: 1, maxRows: 2 }}
                        />
                    </Col>
                    <Col xs={24} sm={12} md={8} lg={6} style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'flex-end' }}>
                        <Button
                            type="primary"
                            onClick={handleDownload}
                            loading={downloadLoading}
                            icon={<DownloadOutlined />}
                            disabled={!dateRange[0] || !dateRange[1]}
                        >
                            Download Report
                        </Button>
                    </Col>
                </Row>
            </Card>
            <TableComponent
                columns={columns}
                dataSource={reportData}
                loading={loading}
                pagination={pagination}
                onChange={handleTableChange}
                scroll={{ y: "calc(100vh - 300px)", x: "max-content" }}
                sticky
            />
        </ReportModernViewWrapper>
    );

};

export default IncomingBagReport; 
